import { useEffect } from "react";
import { create } from "zustand";

import type { Id } from "../api/types";
import type { HeartbeatLocation } from "./liveClient";

/*
  Where the person is, for the heartbeat (2.10): the screen, the item and the session. The address gives a
  first answer; a screen that knows better (the item of a session, say) reports it with `useReportLocation`.
*/

interface Where {
  screen?: string | null;
  itemId?: Id | null;
  sessionId?: Id | null;
  elementId?: Id | null;
  detail?: string | null;
}

interface LocationState {
  route: Where;
  report: Where;
  setRoute(route: Where): void;
  setReport(report: Where): void;
}

export const useLocationStore = create<LocationState>((set) => ({
  route: {},
  report: {},
  setRoute: (route) => set({ route }),
  setReport: (report) => set({ report }),
}));

/** What the heartbeat carries now. */
export function getHeartbeatLocation(): HeartbeatLocation {
  const { route, report } = useLocationStore.getState();
  return {
    screen: report.screen ?? route.screen ?? "unknown",
    item_id: report.itemId ?? route.itemId ?? null,
    session_id: report.sessionId ?? route.sessionId ?? null,
    element_id: report.elementId ?? route.elementId ?? null,
    detail: report.detail ?? null,
  };
}

/** A screen says where the person really is, beyond what the address shows. Cleared when the screen closes. */
export function useReportLocation(where: Where): void {
  const { screen, itemId, sessionId, elementId, detail } = where;
  useEffect(() => {
    useLocationStore.getState().setReport({ screen, itemId, sessionId, elementId, detail });
    return () => useLocationStore.getState().setReport({});
  }, [screen, itemId, sessionId, elementId, detail]);
}
